import React, { useState, useEffect } from 'react';
import { 
  FiPlay, 
  FiPause, 
  FiRotateCcw, 
  FiHome, 
  FiClock, 
  FiTarget,
  FiStar,
  FiCheckCircle
} from 'react-icons/fi';
import { FiX } from 'react-icons/fi';
import { useNotification } from '../../contexts/NotificationContext';

const GamePlayer = ({ game, onClose, onScoreSave }) => {
  const { showSuccess, showError } = useNotification();
  
  const [gameStatus, setGameStatus] = useState('ready'); // ready, playing, paused, completed
  const [score, setScore] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  

  // Timer effect
  useEffect(() => {
    let interval;
    if (gameStatus === 'playing') {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStatus]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startGame = () => {
    setGameStatus('playing');
    setTimeElapsed(0);
    setScore(0);
    setCurrentQuestion(0);
    setAnswers({});
  };

  const pauseGame = () => {
    setGameStatus('paused');
  };

  const resumeGame = () => {
    setGameStatus('playing');
  };

  const resetGame = () => {
    setGameStatus('ready');
    setTimeElapsed(0);
    setScore(0);
    setCurrentQuestion(0);
    setAnswers({});
  };

  const handleAnswer = (questionIndex, answer) => {
    const newAnswers = { ...answers, [questionIndex]: answer };
    setAnswers(newAnswers);
    
    // Calculate score based on correct answers
    const correctAnswers = Object.keys(newAnswers).filter(key => {
      const question = game.questions?.[key];
      return question && question.correctAnswer === newAnswers[key];
    }).length;
    
    const newScore = Math.round((correctAnswers / (game.questions?.length || 1)) * 100);
    setScore(newScore);
    
    // Move to next question or complete game
    if (questionIndex < (game.questions?.length || 1) - 1) {
      setCurrentQuestion(questionIndex + 1);
    } else {
      completeGame(newScore);
    }
  };

  const completeGame = async (finalScore) => {
    setGameStatus('completed');
    setScore(finalScore);
    
    try {
      const response = await fetch('/api/game-scores', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          gameId: game._id,
          score: finalScore,
          timeElapsed: timeElapsed,
          completedAt: new Date().toISOString(),
          answers: answers
        })
      });

      const data = await response.json();
      
      if (data.success) {
        showSuccess('Game completed! Your score has been saved.');
        if (onScoreSave) {
          onScoreSave(data.data.gameProgress);
        }
      } else {
        showError('Game completed, but failed to save score.');
        console.error('Failed to save score:', data.message);
      }
    } catch (error) {
      console.error('Error saving score:', error);
      showError('Game completed, but failed to save score.');
    }
  };



  const renderGameContent = () => {
    if (!game.questions || game.questions.length === 0) {
      return (
        <div className="game-placeholder">
          <FiTarget size={48} />
          <h3>Game Content Coming Soon</h3>
          <p>This game is being prepared by AI. Check back later!</p>
        </div>
      );
    }

    const question = game.questions[currentQuestion];
    
    return (
      <div className="game-content">
        <div className="question-progress">
          <span>Question {currentQuestion + 1} of {game.questions.length}</span>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${((currentQuestion + 1) / game.questions.length) * 100}%` }}
            />
          </div>
        </div>
        
        <div className="question-container">
          <h3>{question.question?.text || question.question?.question || (typeof question.question === 'string' ? question.question : 'Question text not available')}</h3>
          
          <div className="answer-options">
            {question.options?.map((option, index) => (
              <button
                key={index}
                className={`answer-option ${answers[currentQuestion] === option ? 'selected' : ''}`}
                onClick={() => handleAnswer(currentQuestion, option)}
                disabled={gameStatus !== 'playing'}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderGameComplete = () => (
    <div className="game-complete">
      <div className="completion-icon">
        <FiCheckCircle size={64} />
      </div>
      <h2>Game Complete!</h2>
      <div className="final-stats">
        <div className="stat-item">
          <FiTarget />
          <span className="stat-value">{score}%</span>
          <span className="stat-label">Final Score</span>
        </div>
        <div className="stat-item">
          <FiClock />
          <span className="stat-value">{formatTime(timeElapsed)}</span>
          <span className="stat-label">Time Taken</span>
        </div>
        <div className="stat-item">
          <FiStar />
          <span className="stat-value">{Object.keys(answers).length}</span>
          <span className="stat-label">Questions Answered</span>
        </div>
      </div>
      
      <div className="completion-actions">
        <button className="btn btn-secondary" onClick={resetGame}>
          <FiRotateCcw /> Play Again
        </button>
        <button className="btn btn-primary" onClick={onClose}>
          <FiHome /> Back to Games
        </button>
      </div>
    </div>
  );

  return (
    <div className="game-player">
      <div className="game-header">
        <div className="game-info">
          <h1>{game.title}</h1>
          <p>{game.subject} • {game.difficulty}</p>
        </div>
        
        <div className="game-controls">
          <div className="game-stats">
            <div className="stat">
              <FiClock />
              <span>{formatTime(timeElapsed)}</span>
            </div>
            <div className="stat">
              <FiTarget />
              <span>{score}%</span>
            </div>
          </div>
          
          <div className="control-buttons">
            {gameStatus === 'ready' && (
              <button className="btn btn-primary" onClick={startGame}>
                <FiPlay /> Start Game
              </button>
            )}
            
            {gameStatus === 'playing' && (
              <button className="btn btn-secondary" onClick={pauseGame}>
                <FiPause /> Pause
              </button>
            )}
            
            {gameStatus === 'paused' && (
              <>
                <button className="btn btn-primary" onClick={resumeGame}>
                  <FiPlay /> Resume
                </button>
                <button className="btn btn-secondary" onClick={resetGame}>
                  <FiRotateCcw /> Reset
                </button>
              </>
            )}
            
            <button className="btn btn-outline" onClick={onClose}>
              <FiX /> Close
            </button>
          </div>
        </div>
      </div>
      
      <div className="game-body">
        {gameStatus === 'completed' ? renderGameComplete() : renderGameContent()}
      </div>
    </div>
  );
};

export default GamePlayer;